import React, { useState, useRef, useCallback, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@fluentui/react-components";
import EnergyAnalystsDetailGrid from "./energy-analysts-detail-grid";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { LoginContext } from "../../../../../services/api/authentication-api";
import {
  handleStafferAnalystsTimeLogExport,
  getAnalystsTimeLogColumns,
  getInitialDetailState,
  clearDetailStateFromStorage,
} from "../../dashboard-energy-banker/energy-banker-time-log/energy-banker-time-log-helper";
import { normalizeDate } from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import "../../dashboard-energy-banker/energy-banker-time-log/energy-banker-time-log.scss";
import EnergyAnalystsWeightedAvgSummaryGrid from "./energy-analysts-weighted-avg-summary-grid";
import { SubmitEnergyAvailableBankers } from "../../../../../services/api/dashboard-api";

const EnergyAnalystsTimeLog = ({
  energyStafferTimeLogItems,
  energyStafferTimeLogSummaryItems,
  setIsEnergyStafferTimeLogLoading,
  fetchEnergyTechStafferTimeLog,
  setIsEnergyStafferDealResourceLogLoading,
  fetchStafferDealDetailsandResourcingLog,
  showToast,
  isSI = false,
  fetchEnergyJuniorStaffingIndex,
  setIsEnergyJuniorStaffingIndexLoading
}) => {
  const hasMounted = useRef(false);
  const user = useContext(LoginContext);
  const [showDetails, setShowDetails] = useState(true);
  const [detailState, setDetailState] = useState({ sort: [], filter: null });
  const [originalData, setOriginalData] = useState(
    (energyStafferTimeLogItems || []).map((p, index) => ({
      ...p,
      inEdit: true,
      _badgeIndex: index + 1,
      LastUpdatedString: normalizeDate(p.LastUpdated),
    }))
  );
  const [data, setData] = useState(originalData || []);
  const [isVisibleDataEmpty, setIsVisibleDataEmpty] = useState(false);
  const _detailExport = useRef(null);
  const [expandClass, setExpandClass] = useState("");
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [energyStafferTimeLogSummaryItemsState, setEnergyStafferTimeLogSummaryItemsState] = useState(energyStafferTimeLogSummaryItems || {});

  useEffect(() => {
    setEnergyStafferTimeLogSummaryItemsState(energyStafferTimeLogSummaryItems || {});
  }, [energyStafferTimeLogSummaryItems]);

  useEffect(() => {
    if (!hasMounted.current && user?.userID) {
      const initial = getInitialDetailState(user.userID, "analystsTimeLog");
      setDetailState(initial);
      hasMounted.current = true;
    }
  }, [user?.userID]);

  useEffect(() => {
    setIsSubmitDisabled(isSaving || energyStafferTimeLogSummaryItemsState?.AvailableBankersThisWk == energyStafferTimeLogSummaryItems?.AvailableBankersThisWk);
  }, [isSaving, energyStafferTimeLogSummaryItemsState, energyStafferTimeLogSummaryItems]);


  const handleVisibleDataChange = useCallback(({ isEmpty }) => {
    setIsVisibleDataEmpty(isEmpty);
  }, []);

  useEffect(() => {
    const newOriginalData = (energyStafferTimeLogItems || []).map((p, index) => ({
      ...p,
      inEdit: true,
      _badgeIndex: index + 1,
      LastUpdatedString: normalizeDate(p.LastUpdated),
    }));
    setOriginalData(newOriginalData);
    setData(newOriginalData);
  }, [energyStafferTimeLogItems]);

  const hasAnyFilter =
    detailState.filter &&
    Array.isArray(detailState.filter.filters) &&
    detailState.filter.filters.length > 0;

  const canReset = detailState.sort.length > 0 || hasAnyFilter;

  const resetDataState = () => {
    clearDetailStateFromStorage(user?.userID, "analystsTimeLog");
    const resetState = { sort: [], filter: null };
    setDetailState(resetState);
  };

  const expandWidget = () => {
    setExpandClass(expandClass === "" ? "widgetExpanded" : "");
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    const payload = {
      AvailableBankers: energyStafferTimeLogSummaryItemsState?.AvailableBankersThisWk,
      TotalBankers: energyStafferTimeLogSummaryItems?.TotalBankersThisWk ?? 0,
      TaskId: energyStafferTimeLogSummaryItemsState?.ActiveTaskId,
      IsSI: isSI
    };
    try {
      const response = await SubmitEnergyAvailableBankers(payload);
      if (response?.status === 200) {
        showToast(
          "Available Bankers Updated"
        );
      } else {
        showToast("Failed to save Available Bankers", "error");
      }
    } catch (error) {
      showToast("Failed to save Available Bankers", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`energy-banker-time-log clickable-row ${expandClass} dashboard-container-shadow`}>
      <div className="widget-header">
        <div className="header-left">
          <div className="main-title-badge-row">
            <div className="main-title">Analyst & Associate Time Log</div>
          </div>
          <div className="sub-title">{isSI ? 'GIB/Energy/Summer Internship' : 'GIB/Energy'}</div>
        </div>
        <div className="header-right">
          <Tooltip content="Reset Filters and Sorting">
            <span>
              <FluentButton
                appearance="secondaryWithOutline"
                icon={
                  <FluentUiIcon
                    name="ArrowResetRegular"
                    color={canReset ? "#0F6CBD" : "#A0A0A0"}
                    size={20}
                    disabled={!canReset}
                  />
                }
                disabled={!canReset}
                onClick={resetDataState}
              />
            </span>
          </Tooltip>
          <Tooltip content="Export Data">
            <span>
              <FluentButton
                appearance="secondaryWithOutline"
                icon={
                  <FluentUiIcon
                    name="ArrowDownloadRegular"
                    color="#0F6CBD"
                    size={20}
                  />
                }
                onClick={() =>
                  handleStafferAnalystsTimeLogExport({ detailRef: _detailExport })
                }
              />
            </span>
          </Tooltip>
          <Tooltip content="Expand Widget">
            <span>
              <FluentButton
                appearance="secondaryWithOutline"
                icon={
                  <FluentUiIcon
                    name={
                      expandClass === "widgetExpanded"
                        ? "FullScreenMinimizeFilled"
                        : "FullScreenMaximizeFilled"
                    }
                    color={"#0F6CBD"}
                    size={20}
                  />
                }
                onClick={expandWidget}
              />
            </span>
          </Tooltip>
          <FluentButton
            id="tb-submit-btn"
            text="Submit"
            appearance="primary"
            className={"ml-2"}
            icon={
              <FluentUiIcon
                name="DocumentArrowUpRegular"
                size={20}
                color={isSubmitDisabled ? "#A0A0A0" : "#FFFFFF"}
              />
            }
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          />
        </div>
      </div>
      {showDetails && (
        <React.Fragment>
          <ExcelExport data={data} ref={_detailExport}>
            {getAnalystsTimeLogColumns()}
          </ExcelExport>
          <EnergyAnalystsWeightedAvgSummaryGrid summaryItem={energyStafferTimeLogSummaryItemsState} setEnergyStafferTimeLogSummaryItemsState={setEnergyStafferTimeLogSummaryItemsState} />
          <EnergyAnalystsDetailGrid
            data={data}
            setIsEnergyStafferTimeLogLoading={setIsEnergyStafferTimeLogLoading}
            fetchEnergyTechStafferTimeLog={fetchEnergyTechStafferTimeLog}
            setIsEnergyStafferDealResourceLogLoading={setIsEnergyStafferDealResourceLogLoading}
            fetchStafferDealDetailsandResourcingLog={fetchStafferDealDetailsandResourcingLog}
            detailState={detailState}
            onDetailStateChange={setDetailState}
            onVisibleDataChange={handleVisibleDataChange}
            resetDataState={resetDataState}
            showToast={showToast}
            isSI={isSI}
            fetchEnergyJuniorStaffingIndex={fetchEnergyJuniorStaffingIndex}
            setIsEnergyJuniorStaffingIndexLoading={setIsEnergyJuniorStaffingIndexLoading}
          />
          {/* Following div is added to bring focus to submit button if the user is tabbing through the widget */}
          <div
            tabIndex={0}
            style={{ width: 0, height: 0, overflow: "hidden" }}
            onFocus={() => {
              const btn = document.getElementById("tb-submit-btn");
              if (btn) btn.focus();
            }}
          />
        </React.Fragment>
      )}
    </div>
  );
};

EnergyAnalystsTimeLog.propTypes = {
  energyStafferTimeLogItems: PropTypes.arrayOf(
    PropTypes.shape({
      UserId: PropTypes.number,
      Name: PropTypes.string,
      Level: PropTypes.string,
      TotalHoursLastWeek: PropTypes.number,
      TotalWeekendHoursLastWeek: PropTypes.number,
      TotalActualHoursLastWeek: PropTypes.number,
      TotalPriorWeekEstimate: PropTypes.number,
      TotalHoursCurrentWeek: PropTypes.number,
      LastUpdated: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
    })
  ).isRequired,
  energyStafferTimeLogSummaryItems: PropTypes.shape({
    AvailableBankersThisWk: PropTypes.number,
    TotalBankersThisWk: PropTypes.number,
    ActiveTaskId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  setIsEnergyStafferTimeLogLoading: PropTypes.func,
  fetchEnergyTechStafferTimeLog: PropTypes.func,
  setIsEnergyStafferDealResourceLogLoading: PropTypes.func,
  fetchStafferDealDetailsandResourcingLog: PropTypes.func,
  showToast: PropTypes.func,
  isSI: PropTypes.bool,
  fetchEnergyJuniorStaffingIndex: PropTypes.func,
  setIsEnergyJuniorStaffingIndexLoading: PropTypes.func,
};

export default EnergyAnalystsTimeLog;
